<script setup lang="ts">
/**
 * Страницы базы знаний, привязанные к задаче.
 *
 * В макете карточки (13:401) этого блока нет — привязку со стороны задачи там
 * не нарисовали (docs/design-gaps.md). Сделан по образцу вложений: подпись,
 * плитки со снятием на наведении и тихая строка «Привязать страницу».
 *
 * Связь хранится на стороне страницы, поэтому и заводится, и снимается ручками
 * knowledge — трекер о ней не знает.
 */
import { Book, Plus, X } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  linkIssue,
  listIssuePages,
  searchPages,
  unlinkIssue,
  type PageBrief,
  type PageHit,
} from '@/features/knowledge/api'

import { useIssueStore } from '../store'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const pages = ref<PageBrief[]>([])
const isPicking = ref(false)
const query = ref('')
const hits = ref<PageHit[]>([])
const busy = ref(false)

const issueKey = computed(() => store.issue?.key ?? '')

const load = async () => {
  if (!issueKey.value) return

  try {
    pages.value = await listIssuePages(issueKey.value)
  } catch (failure) {
    emit('failed', failure)
  }
}

/** Уже привязанные в подсказках не показываем: связь всё равно не создастся. */
const suggestions = computed(() =>
  hits.value.filter((hit) => !pages.value.some((page) => page.id === hit.id)),
)

const search = async () => {
  const needle = query.value.trim()

  if (needle.length < 2) {
    hits.value = []

    return
  }

  try {
    hits.value = await searchPages(needle)
  } catch (failure) {
    emit('failed', failure)
  }
}

const attach = async (hit: PageHit) => {
  busy.value = true

  try {
    await linkIssue(hit.id, issueKey.value)
    await load()
    isPicking.value = false
    query.value = ''
    hits.value = []
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}

const detach = async (page: PageBrief) => {
  try {
    await unlinkIssue(page.id, issueKey.value)
    await load()
  } catch (failure) {
    emit('failed', failure)
  }
}

watch(issueKey, load, { immediate: true })
</script>

<template>
  <section class="ipages">
    <span v-if="pages.length" class="ipages__label">{{ t('issue.pages.title') }}</span>

    <div v-if="pages.length" class="ipages__grid">
      <div v-for="page in pages" :key="page.id" class="pchip">
        <RouterLink class="pchip__open" :to="`/kb/${page.id}`" :title="page.title">
          <Book class="pchip__icon" :size="14" />
          <span class="pchip__name">{{ page.title }}</span>
        </RouterLink>

        <button
          type="button"
          class="pchip__off"
          :aria-label="t('issue.pages.remove', { title: page.title })"
          @click="detach(page)"
        >
          <X :size="12" />
        </button>
      </div>
    </div>

    <button v-if="!isPicking" type="button" class="ipages__add" @click="isPicking = true">
      <Plus :size="14" />
      {{ t('issue.pages.add') }}
    </button>

    <div v-else class="ipages__picker">
      <input
        v-model="query"
        class="ipages__field"
        :placeholder="t('issue.pages.search')"
        :aria-label="t('issue.pages.search')"
        autofocus
        @input="search"
        @keydown.esc="isPicking = false"
      />

      <ul v-if="suggestions.length" class="ipages__hits">
        <li v-for="hit in suggestions" :key="hit.id">
          <button type="button" class="ipages__hit" :disabled="busy" @click="attach(hit)">
            <span class="ipages__hit-title">{{ hit.title }}</span>
            <span v-if="hit.snippet" class="ipages__hit-snippet">{{ hit.snippet }}</span>
          </button>
        </li>
      </ul>

      <p v-else-if="query.trim().length >= 2" class="ipages__none">
        {{ t('issue.pages.nothing') }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.ipages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ipages__label {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.ipages__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pchip {
  display: flex;
  align-items: center;
  height: 32px;
  padding-right: 4px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.pchip__open {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 0 4px 0 10px;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.pchip__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.pchip__name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Крестик проявляется на наведении — как на связях задачи: постоянный он
   превращает список ссылок в список кнопок удаления. */
.pchip__off {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  opacity: 0;
  cursor: pointer;
}

.pchip:hover .pchip__off,
.pchip__off:focus-visible {
  opacity: 1;
}

.pchip__off:hover {
  color: var(--color-danger);
}

.ipages__add {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.ipages__add:hover {
  color: var(--color-text-primary);
}

.ipages__picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 420px;
}

.ipages__field {
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.ipages__field:focus {
  border-color: var(--color-primary);
  outline: none;
}

.ipages__hits {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  list-style: none;
}

.ipages__hit {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  font-family: var(--font-family-sans);
  text-align: left;
  cursor: pointer;
}

.ipages__hit:hover {
  background: var(--color-bg-muted);
}

.ipages__hit-title {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
}

.ipages__hit-snippet {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.ipages__none {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
